import type { SimulationData } from '@/api';
import { SimulationStatus } from '@/api';
import { FullSizeSpinner } from '@/components/full-size-spinner';
import { PageWrapper } from '@/components/page-wrapper/page-wrapper';
import type { LockableField } from '@/components/table/table-types';
import { useAuthContext } from '@/context/auth-context';
import { useNotification } from '@/hooks/use-notification';
import { useSimulationErrorNotification } from '@/hooks/use-simulation-error-notification';
import PriceDownloadModal from '@/pages/price-planning/create-price-page/components/price-download-modal';
import type { DownloadOption } from '@/pages/price-planning/create-price-page/types/create-price-table-types';
import { ROUTES } from '@/routes';
import { normalizeApiError } from '@/utils/error-utils';
import { findZeroLpViolations } from '@/utils/lp-validation';
import { Flex, Spinner } from '@lego/klik-ui';
import { useQueryClient } from '@tanstack/react-query';
import type { MRT_ColumnFiltersState } from 'material-react-table';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  usePreviewSimulationVersion,
  usePricelistSettings,
  usePublishSimulation,
  useSimulationData,
  useSimulationDetails,
  useSimulationVersions,
  useUpdateSimulationAssignment,
  useUpdateWatchers,
  useVersionPricelistSettings,
} from '../simulation-queries';
import { useSimulationQueryErrorNotification } from '../use-simulation-query-error-notification';
import HarmonizationPivotTable from './components/harmonization/harmonization-pivot-table';
import SimulationHeaderActions from './components/header/simulation-header-actions';
import SimulationHeaderTitle from './components/header/simulation-header-title';
import AddRemoveWatchersModal from './components/modals/add-remove-watchers-modal';
import AssignToModal from './components/modals/assign-to-modal';
import SendToModal from './components/modals/send-to-modal';
import PriceSettingsModal from './components/price-settings-modal/price-settings-modal';
import { PublishSimulationModal } from './components/publish-simulation-modal';
import SimulationIndexSection from './components/simulation-index-section/simulation-index-section';
import SimulationTable from './components/simulation-table';
import type { UploadedPriceRow } from './components/upload/helpers';
import SimulationUploadModal from './components/upload/simulation-upload-modal';
import { useSimulationReadOnly } from './context/simulation-read-only-context';
import KeyfiguresView from './keyfigures-report/keyfigures-view';
import { SimulationView } from './simulation-view-constants';
import {
  buildOriginalBaselineMap,
  buildPricelistSettingsMap,
  calculateAverageVat,
  computeSimulationIndexes,
  groupDataByPriceList,
  mapSimulationDataToRowData,
  mergePreviewRowWithCurrentRow,
} from './utils/simulation-helpers';
import type {
  PricelistSettings,
  ReviewerAssignmentRequest,
  RowData,
} from './utils/simulation-types';
import { SIM_MODAL } from './utils/simulation-types';

const isNon404Error = (isError: boolean, error: unknown) =>
  isError && error != null && normalizeApiError(error).status !== 404;

const SimulationToolPage = () => {
  const numericSimulationId = Number(useParams().simulationId);
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showNotification = useNotification();
  const showSimulationError = useSimulationErrorNotification();

  const {
    isVersionHistoryOpen,
    selectedVersionId,
    setSelectedVersionId,
    setIsViewerMode,
    isReadOnly,
  } = useSimulationReadOnly();

  const {
    data: simulationDetails,
    isError: isSimulationDetailsError,
    error: simulationDetailsError,
  } = useSimulationDetails(numericSimulationId);

  const isSimulationIdInvalid =
    !Number.isFinite(numericSimulationId) || numericSimulationId <= 0;

  // 403 is treated the same as 404 so users without access land on the not-found page
  const isSimulationNotFound =
    isSimulationIdInvalid ||
    (isSimulationDetailsError &&
      [403, 404].includes(normalizeApiError(simulationDetailsError).status));

  const {
    data: simulationData,
    isSuccess: isSimulationDataSuccess,
    isError: isSimulationDataError,
    error: simulationDataError,
  } = useSimulationData(numericSimulationId, !isSimulationNotFound);

  const {
    data: pricelistSettings,
    isError: isPricelistSettingsError,
    error: pricelistSettingsError,
  } = usePricelistSettings(numericSimulationId, isSimulationDataSuccess);

  const {
    data: previewData,
    isFetching: isFetchingPreview,
    isError: isPreviewSimulationVersionError,
    error: previewSimulationVersionError,
  } = usePreviewSimulationVersion(numericSimulationId, selectedVersionId);

  const {
    data: versionPriceSettings,
    isFetching: isFetchingVersionSettings,
    isError: isVersionPricelistSettingsError,
    error: versionPricelistSettingsError,
  } = useVersionPricelistSettings(numericSimulationId, selectedVersionId);

  const {
    data: versions,
    isError: isSimulationVersionsError,
    error: simulationVersionsError,
  } = useSimulationVersions(numericSimulationId, isSimulationDataSuccess);

  const priceSettingsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const assignToTriggerRef = useRef<HTMLButtonElement | null>(null);
  const sendToTriggerRef = useRef<HTMLButtonElement | null>(null);
  const addRemoveWatchersTriggerRef = useRef<HTMLButtonElement | null>(null);
  const downloadTriggerRef = useRef<HTMLLIElement | null>(null);
  const publishTriggerRef = useRef<HTMLButtonElement | null>(null);
  const uploadApplyRef = useRef<((rows: UploadedPriceRow[]) => void) | null>(
    null,
  );

  const [selectedView, setSelectedView] = useState<SimulationView>(
    SimulationView.BASIC,
  );
  const [userGroupPreference, setUserGroupPreference] = useState<
    'simple' | 'lel'
  >('simple');
  const [showModal, setShowModal] = useState<null | SIM_MODAL>(null);
  const [lockedColumn, setLockedColumn] = useState<LockableField | undefined>(
    undefined,
  );
  const [lpErrorCells, setLpErrorCells] = useState<Set<string>>(new Set());
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );

  const [pricelistSettingsData, setPricelistSettingsData] = useState<
    Map<string, PricelistSettings>
  >(new Map());

  const [initialSimulationDataMap, setInitialSimulationDataMap] = useState<
    Map<string, RowData>
  >(new Map());

  const [currentSimulationData, setCurrentSimulationData] = useState<
    Map<string, RowData>
  >(new Map());

  const initialPricelistSettingsMap = useMemo(() => {
    const groupedData = groupDataByPriceList(
      (simulationData?.simulationData || []) as unknown as RowData[],
    );
    return new Map(
      (pricelistSettings?.priceSettings || []).map((setting) => {
        const priceUnitId = (setting.priceUnitId ?? 0).toString();
        return [
          priceUnitId,
          {
            ...setting,
            averageVat: calculateAverageVat(groupedData[priceUnitId] || []),
          },
        ];
      }),
    );
  }, [pricelistSettings?.priceSettings, simulationData?.simulationData]);

  useEffect(() => {
    if (!simulationDetails) return;
    const isOwner =
      user?.userId != null && simulationDetails.assignedTo.id === user.userId;
    // Published is terminal — read-only for everyone, including the assignee (spec §5.2).
    const isPublished = simulationDetails.status === SimulationStatus.Published;
    setIsViewerMode(!isOwner || isPublished);
  }, [simulationDetails, user?.userId, setIsViewerMode]);

  useEffect(() => {
    if (!simulationData?.simulationData) return;
    setPricelistSettingsData((prev) => {
      if (prev.size === 0) return initialPricelistSettingsMap;
      return new Map(
        Array.from(initialPricelistSettingsMap, ([id, initial]) => [
          id,
          prev.get(id) ?? initial,
        ]),
      );
    });
  }, [simulationData, initialPricelistSettingsMap]);

  useEffect(() => {
    if (
      simulationData?.simulationData &&
      initialPricelistSettingsMap.size > 0 &&
      initialSimulationDataMap.size === 0
    ) {
      const data = mapSimulationDataToRowData(
        simulationData.simulationData,
        initialPricelistSettingsMap,
      );
      setInitialSimulationDataMap(
        new Map(data.map((row) => [row.primaryKey, row])),
      );
    }
  }, [
    simulationData?.simulationData,
    initialPricelistSettingsMap,
    initialSimulationDataMap.size,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pricelistSettingsData is intentionally excluded to avoid overwriting user edits
  useEffect(() => {
    if (
      simulationData?.simulationData &&
      simulationData.simulationData.length > 0
    ) {
      const data = mapSimulationDataToRowData(
        simulationData.simulationData,
        pricelistSettingsData,
      );
      setCurrentSimulationData(
        new Map(data.map((row) => [row.primaryKey, row])),
      );
    }
  }, [simulationData]);

  // Determine which version is the diff baseline:
  //   - Version panel open, specific version => the version created just before it
  //   - Version panel open, "Current"        => the most recently saved version
  //   - Version panel closed                 => null (no pink mode, no extra fetches)
  const previousVersionId = useMemo<number | null>(() => {
    if (!isVersionHistoryOpen) return null;
    if (!versions?.length) return null;
    const sorted = [...versions].sort(
      (a, b) => (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0),
    );
    if (selectedVersionId === null) {
      return sorted[sorted.length - 1]?.id ?? null;
    }
    const idx = sorted.findIndex((v) => v.id === selectedVersionId);
    return idx > 0 ? (sorted[idx - 1]?.id ?? null) : null;
  }, [isVersionHistoryOpen, versions, selectedVersionId]);

  const {
    data: prevVersionData,
    isFetching: isFetchingPrevVersion,
    isError: isPreviousVersionPreviewError,
    error: previousVersionPreviewError,
  } = usePreviewSimulationVersion(numericSimulationId, previousVersionId);

  const {
    data: prevVersionPriceSettings,
    isFetching: isFetchingPrevVersionSettings,
    isError: isPreviousVersionPricelistSettingsError,
    error: previousVersionPricelistSettingsError,
  } = useVersionPricelistSettings(numericSimulationId, previousVersionId);

  useSimulationQueryErrorNotification({
    isError: isSimulationDetailsError && !isSimulationNotFound,
    error: simulationDetailsError,
    title: 'Failed to load simulation details',
  });

  useSimulationQueryErrorNotification({
    isError: isPricelistSettingsError && !isSimulationNotFound,
    error: pricelistSettingsError,
    title: 'Failed to load price settings',
  });

  useSimulationQueryErrorNotification({
    isError: isSimulationDataError && !isSimulationNotFound,
    error: simulationDataError,
    title: 'Failed to load simulation data',
  });

  useSimulationQueryErrorNotification({
    isError: isSimulationVersionsError && !isSimulationNotFound,
    error: simulationVersionsError,
    title: 'Failed to load simulation versions',
  });

  // Version preview 404s are handled by the useEffect below (clears
  // selectedVersionId). Suppress the generic "Not found" toast — the version
  // simply isn't previewable, not a user-facing error.
  useSimulationQueryErrorNotification({
    isError: isNon404Error(
      isPreviewSimulationVersionError,
      previewSimulationVersionError,
    ),
    error: previewSimulationVersionError,
    title: 'Failed to load version preview',
  });

  useSimulationQueryErrorNotification({
    isError: isNon404Error(
      isVersionPricelistSettingsError,
      versionPricelistSettingsError,
    ),
    error: versionPricelistSettingsError,
    title: 'Failed to load version price settings',
  });

  useSimulationQueryErrorNotification({
    isError: isNon404Error(
      isPreviousVersionPreviewError,
      previousVersionPreviewError,
    ),
    error: previousVersionPreviewError,
    title: 'Failed to load previous version preview',
  });

  useSimulationQueryErrorNotification({
    isError: isNon404Error(
      isPreviousVersionPricelistSettingsError,
      previousVersionPricelistSettingsError,
    ),
    error: previousVersionPricelistSettingsError,
    title: 'Failed to load previous version price settings',
  });

  // Clear selected version only on 404 (snapshot data missing). Transient
  // errors (500) keep the selection so the user can retry.
  useEffect(() => {
    if (
      isPreviewSimulationVersionError &&
      selectedVersionId !== null &&
      normalizeApiError(previewSimulationVersionError).status === 404
    ) {
      setSelectedVersionId(null);
    }
  }, [
    isPreviewSimulationVersionError,
    previewSimulationVersionError,
    selectedVersionId,
    setSelectedVersionId,
  ]);

  // using deferred values to avoid UI freezes while processing large datasets
  const deferredPreviewData = useDeferredValue(previewData);
  const deferredVersionPriceSettings = useDeferredValue(versionPriceSettings);
  const deferredPrevVersionData = useDeferredValue(prevVersionData);
  const deferredPrevVersionPriceSettings = useDeferredValue(
    prevVersionPriceSettings,
  );

  // True while React is still working through the deferred render pass.
  const isTransitioning =
    deferredPreviewData !== previewData ||
    deferredPrevVersionData !== prevVersionData;

  // Build a PricelistSettings map from the version's saved price settings,
  // computing averageVat from the version's simulation data rows.
  const previewPricelistSettingsMap = useMemo(
    () =>
      buildPricelistSettingsMap(
        deferredVersionPriceSettings?.priceSettings,
        deferredPreviewData?.simulationData,
      ),
    [
      deferredVersionPriceSettings?.priceSettings,
      deferredPreviewData?.simulationData,
    ],
  );

  // Build a PricelistSettings map for the previous version (diff baseline).
  const previousVersionPricelistSettingsMap = useMemo(
    () =>
      buildPricelistSettingsMap(
        deferredPrevVersionPriceSettings?.priceSettings,
        deferredPrevVersionData?.simulationData,
      ),
    [
      deferredPrevVersionPriceSettings?.priceSettings,
      deferredPrevVersionData?.simulationData,
    ],
  );

  // Use version settings when previewing, otherwise fall back to live settings.
  const activeSettings = previewPricelistSettingsMap ?? pricelistSettingsData;

  // All four async requests must complete before showing diff highlights to
  // avoid partial-render flicker while the previous version data loads.
  // isTransitioning extends the spinner through the deferred computation phase
  // so cached-data switches don't appear as a freeze with no feedback.
  const isFetchingPreviewData =
    isVersionHistoryOpen &&
    (isFetchingPreview ||
      isFetchingVersionSettings ||
      isFetchingPrevVersion ||
      isFetchingPrevVersionSettings ||
      isTransitioning);

  // Shared lookup used by both version data-map memos below to fill in
  // frozen fields that the backend returns as 0 in preview responses.
  const currentRowByKey = useMemo(
    () =>
      new Map<string, SimulationData>(
        (simulationData?.simulationData ?? []).map((row) => [
          row.primaryKey ?? '',
          row,
        ]),
      ),
    [simulationData?.simulationData],
  );

  // When a version is selected, build a RowData map from the preview response
  // and display it instead of the live simulation data.
  const previewSimulationDataMap = useMemo(() => {
    if (!deferredPreviewData?.simulationData) return null;
    const mergedPreviewRows = deferredPreviewData.simulationData.map(
      (previewRow) =>
        mergePreviewRowWithCurrentRow(
          previewRow,
          currentRowByKey.get(previewRow.primaryKey ?? ''),
        ),
    );
    const rows = mapSimulationDataToRowData(mergedPreviewRows, activeSettings);
    return new Map(rows.map((row) => [row.primaryKey, row]));
  }, [deferredPreviewData, currentRowByKey, activeSettings]);

  // Build a RowData map from the previous version, merging frozen fields from
  // the live rows. Used as the pink-highlight baseline for the simulation table
  // and the index section.
  const previousVersionBaseDataMap = useMemo(() => {
    if (
      !deferredPrevVersionData?.simulationData ||
      !previousVersionPricelistSettingsMap
    )
      return undefined;
    const mergedRows = deferredPrevVersionData.simulationData.map((prevRow) =>
      mergePreviewRowWithCurrentRow(
        prevRow,
        currentRowByKey.get(prevRow.primaryKey ?? ''),
      ),
    );
    const rows = mapSimulationDataToRowData(
      mergedRows,
      previousVersionPricelistSettingsMap,
    );
    return new Map(rows.map((row) => [row.primaryKey, row]));
  }, [
    deferredPrevVersionData?.simulationData,
    previousVersionPricelistSettingsMap,
    currentRowByKey,
  ]);

  const { mutateAsync: publishSimulation, isPending: isPublishing } =
    usePublishSimulation(
      () => {
        showNotification({
          type: 'success',
          title: 'Prices saved',
          description: 'The simulation prices have been saved successfully.',
        });
        queryClient.invalidateQueries({
          queryKey: ['simulation', numericSimulationId],
        });
        queryClient.invalidateQueries({
          queryKey: ['simulationVersions', numericSimulationId],
        });
        setShowModal(null);
        navigate(ROUTES.SimulationList.path);
      },
      (error) => {
        showSimulationError(error, 'publish simulation');
      },
    );

  const handleConfirmPublish = useCallback(async () => {
    if (!simulationDetails) return;

    const violations = findZeroLpViolations(
      [...currentSimulationData.values()],
      initialSimulationDataMap,
    );

    if (violations.length > 0) {
      setLpErrorCells(new Set(violations.map((r) => r.primaryKey)));
      showNotification({
        type: 'error',
        title: 'Oops!',
        description:
          'Saving failed because List Price(s) must be greater than 0.',
      });
      setShowModal(null);
      return;
    }

    // Publishing from Draft or Local Review skips local input —
    // send confirmed flag so backend knows the user acknowledged this.
    const needsConfirmation =
      simulationDetails.status === SimulationStatus.Draft ||
      simulationDetails.status === SimulationStatus.LocalReview;

    await publishSimulation({
      simulationId: simulationDetails.id,
      confirmed: needsConfirmation ? true : undefined,
    });
  }, [
    publishSimulation,
    simulationDetails,
    currentSimulationData,
    initialSimulationDataMap,
    showNotification,
  ]);

  const handleClearLpErrorCell = useCallback((primaryKey: string) => {
    setLpErrorCells((prev) => {
      if (!prev.has(primaryKey)) return prev;
      const next = new Set(prev);
      next.delete(primaryKey);
      return next;
    });
  }, []);

  const simulationExportRef = useRef<((type: DownloadOption) => void) | null>(
    null,
  );

  const handleExportData = useCallback((downloadType: DownloadOption) => {
    simulationExportRef.current?.(downloadType);
    setShowModal(null);
  }, []);

  const { mutate: updateAssignment, isPending: isUpdatingAssignment } =
    useUpdateSimulationAssignment(
      () => {
        queryClient.invalidateQueries({
          queryKey: ['simulation', numericSimulationId],
        });
        queryClient.invalidateQueries({
          queryKey: ['simulationVersions', numericSimulationId],
        });
        queryClient.invalidateQueries({
          queryKey: ['simulationWatchers', numericSimulationId],
        });
        queryClient.invalidateQueries({
          queryKey: ['watcherCandidates', numericSimulationId],
        });
        queryClient.invalidateQueries({ queryKey: ['simulationList'] });
        showNotification({
          type: 'success',
          title:
            showModal === SIM_MODAL.AssignTo
              ? 'Assignee changed'
              : 'Status changed',
          description: 'The simulation has been updated successfully.',
        });
      },
      (error) => {
        showSimulationError(
          error,
          showModal === SIM_MODAL.AssignTo
            ? 'reassign simulation'
            : 'update status',
        );
      },
    );

  const { mutate: updateWatchers, isPending: isUpdatingWatchers } =
    useUpdateWatchers((error) => {
      showSimulationError(error, 'update watchers');
    });

  const handleAddRemoveWatchers = (userIds: string[]) => {
    updateWatchers(
      { simulationId: numericSimulationId, userIds },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['simulationWatchers', numericSimulationId],
          });
          queryClient.invalidateQueries({ queryKey: ['simulationList'] });
          setShowModal(null);
        },
      },
    );
  };

  const handleUpdateAssignment = (request: ReviewerAssignmentRequest) => {
    if (!simulationDetails) return;
    updateAssignment(
      {
        simulationId: simulationDetails.id,
        body: {
          newAssignedToId: request.assignment.newAssignedToId,
          message: request.assignment.message,
        },
      },
      {
        onSuccess: () =>
          setShowModal(
            request.afterAssign === 'add-watchers'
              ? SIM_MODAL.AddRemoveWatchers
              : null,
          ),
      },
    );
  };

  // ── Derived display data ──────────────────────────────────────────────────
  const displaySimulationData =
    previewSimulationDataMap ?? currentSimulationData;

  const asIsSimulationRows = useMemo(
    () =>
      Array.from(buildOriginalBaselineMap(initialSimulationDataMap).values()),
    [initialSimulationDataMap],
  );

  const asIsComputedIndexes = useMemo(
    () =>
      computeSimulationIndexes(
        asIsSimulationRows,
        initialPricelistSettingsMap,
        simulationDetails?.planningPriceUnits || [],
      ),
    [
      asIsSimulationRows,
      initialPricelistSettingsMap,
      simulationDetails?.planningPriceUnits,
    ],
  );

  const computedIndexes = useMemo(
    () =>
      computeSimulationIndexes(
        Array.from(displaySimulationData.values()),
        activeSettings,
        simulationDetails?.planningPriceUnits || [],
      ),
    [
      displaySimulationData,
      activeSettings,
      simulationDetails?.planningPriceUnits,
    ],
  );

  // Compute indexes for the previous version so the index section can
  // highlight (pink) what changed between versions.
  const previousVersionComputedIndexes = useMemo(() => {
    if (!previousVersionBaseDataMap || !previousVersionPricelistSettingsMap)
      return undefined;
    return computeSimulationIndexes(
      Array.from(previousVersionBaseDataMap.values()),
      previousVersionPricelistSettingsMap,
      simulationDetails?.planningPriceUnits || [],
    );
  }, [
    previousVersionBaseDataMap,
    previousVersionPricelistSettingsMap,
    simulationDetails?.planningPriceUnits,
  ]);

  //needed for calculations in the "big" table
  const totalLlRrpIndexMap = useMemo(() => {
    const indexMap = new Map<string, number>();
    for (const row of computedIndexes) {
      indexMap.set(row.priceList.id.toString(), row.rrpIndex);
    }
    return indexMap;
  }, [computedIndexes]);

  // ── Table height (driven by the index section's dynamic height) ───────────
  const [indexTableNode, setIndexTableNode] = useState<HTMLDivElement | null>(
    null,
  );

  const groupedBy =
    selectedView === SimulationView.RM_REPORT
      ? 'rm'
      : selectedView === SimulationView.RRP_REPORT
        ? 'rrp'
        : undefined;

  const isPublishDisabled =
    simulationDetails?.status === SimulationStatus.Published ||
    simulationDetails?.isPublishingOfPricesBlocked;

  const sharedToolbarProps = {
    selectedView,
    setSelectedView,
    setShowModal,
    isPublishDisabled: isPublishDisabled ?? false,
    publishTriggerRef,
  };

  return (
    <>
      {simulationDetails !== undefined && currentSimulationData.size > 0 ? (
        <PageWrapper
          headerProps={{
            title: (
              <SimulationHeaderTitle
                simulationDetails={simulationDetails}
                sendToTriggerRef={sendToTriggerRef}
                onSendToClick={() => setShowModal(SIM_MODAL.SendTo)}
              />
            ),
            actions: (
              <SimulationHeaderActions
                simulationDetails={simulationDetails}
                hasPricelistSettings={pricelistSettingsData.size > 0}
                priceSettingsTriggerRef={priceSettingsTriggerRef}
                onPriceSettingsClick={() =>
                  setShowModal(SIM_MODAL.PriceSettings)
                }
                watchersTriggerRef={addRemoveWatchersTriggerRef}
                onWatchersClick={() =>
                  setShowModal(SIM_MODAL.AddRemoveWatchers)
                }
                assignToTriggerRef={assignToTriggerRef}
                onAssignToClick={() => setShowModal(SIM_MODAL.AssignTo)}
              />
            ),
          }}
          maxWidth="100%"
          contentPaddingX={0}
          layout="fill"
        >
          <>
            {isFetchingPreviewData && (
              <Flex
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={10}
                justifyContent="center"
                alignItems="center"
                backgroundColor="rgba(255,255,255,0.6)"
              >
                <Spinner size="lg" />
              </Flex>
            )}
            {selectedView === SimulationView.BASIC && (
              <SimulationIndexSection
                isReadOnly={isReadOnly}
                ref={setIndexTableNode}
                formattedInitialData={asIsComputedIndexes}
                calculatedData={computedIndexes}
                pricelistSettings={activeSettings}
                simulationId={simulationDetails.id}
                setPricelistSettings={setPricelistSettingsData}
                liveComputedIndexes={
                  // Pink mode: only when the version history panel is open.
                  // Empty array for oldest version (no predecessor) — activates
                  // pink mode but produces zero matches.
                  isVersionHistoryOpen
                    ? (previousVersionComputedIndexes ?? [])
                    : undefined
                }
              />
            )}
            {selectedView === SimulationView.HARMONIZATION ? (
              <HarmonizationPivotTable
                isReadOnly={isReadOnly}
                data={displaySimulationData}
                simulationDetails={simulationDetails}
                pricelistSettings={activeSettings}
                selectedView={selectedView}
                setSelectedView={setSelectedView}
                setShowModal={setShowModal}
                isPublishDisabled={isPublishDisabled ?? false}
                publishTriggerRef={publishTriggerRef}
              />
            ) : selectedView === SimulationView.KEYFIGURES ? (
              <KeyfiguresView
                simulationRows={displaySimulationData}
                simulationSettings={activeSettings}
                asIsSimulationRows={asIsSimulationRows}
                asIsSettings={initialPricelistSettingsMap}
                planningYear={simulationDetails.planningYear}
                priceUnits={simulationDetails.planningPriceUnits ?? []}
                totalLlRrpIndex={totalLlRrpIndexMap}
                toolbarProps={{
                  ...sharedToolbarProps,
                  lockedColumn,
                  setLockedColumn,
                  simulationId: simulationDetails.id,
                  downloadTriggerRef,
                  isLel: userGroupPreference === 'lel',
                  setPreference: setUserGroupPreference,
                  columnFilters,
                  setColumnFilters,
                }}
              />
            ) : (
              <SimulationTable
                {...sharedToolbarProps}
                isReadOnly={isReadOnly}
                initialData={initialSimulationDataMap}
                simulationData={displaySimulationData}
                previewBaseData={
                  // Pink mode: only when the version history panel is open.
                  // Empty Map for oldest version (no predecessor) — activates
                  // pink mode but produces zero matches, suppressing orange.
                  isVersionHistoryOpen
                    ? (previousVersionBaseDataMap ?? new Map())
                    : undefined
                }
                pricelistSettings={activeSettings}
                onDataChange={(data) => {
                  // Don't overwrite live data while previewing a version
                  if (!previewSimulationDataMap) {
                    setCurrentSimulationData(data);
                  }
                }}
                lockedColumn={lockedColumn}
                setLockedColumn={setLockedColumn}
                totalLlRrpIndex={totalLlRrpIndexMap}
                planningYear={simulationDetails.planningYear}
                simulationId={simulationDetails.id}
                indexTableNode={indexTableNode}
                groupedBy={groupedBy}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                onBeforeDownload={(exportFn) => {
                  simulationExportRef.current = exportFn;
                }}
                onUploadReady={(fn) => {
                  uploadApplyRef.current = fn;
                }}
                downloadTriggerRef={downloadTriggerRef}
                lpErrorCells={lpErrorCells}
                onClearLpErrorCell={handleClearLpErrorCell}
                preference={userGroupPreference}
                setPreference={setUserGroupPreference}
              />
            )}
          </>
          <PriceSettingsModal
            isOpen={showModal === SIM_MODAL.PriceSettings}
            triggerRef={priceSettingsTriggerRef}
            onClose={() => setShowModal(null)}
            simulationId={simulationDetails.id}
            pricelistSettings={pricelistSettingsData}
            priceUnits={simulationDetails.planningPriceUnits ?? []}
            onApply={(updatedSettings) => {
              setPricelistSettingsData(updatedSettings);
              setShowModal(null);
            }}
          />
          <SendToModal
            isOpen={showModal === SIM_MODAL.SendTo}
            simulationId={simulationDetails.id}
            simulationStatus={simulationDetails.status}
            triggerRef={sendToTriggerRef}
            onClose={() => setShowModal(null)}
            onConfirm={handleUpdateAssignment}
            isSending={isUpdatingAssignment}
          />
          <AssignToModal
            isOpen={showModal === SIM_MODAL.AssignTo}
            simulationId={simulationDetails.id}
            triggerRef={assignToTriggerRef}
            onClose={() => setShowModal(null)}
            onConfirm={(newAssignedToId) =>
              handleUpdateAssignment({
                assignment: {
                  newAssignedToId,
                  message: '',
                },
                afterAssign: 'close',
              })
            }
            isSending={isUpdatingAssignment}
            simulationStatus={simulationDetails.status}
          />
          <AddRemoveWatchersModal
            isOpen={showModal === SIM_MODAL.AddRemoveWatchers}
            simulationId={simulationDetails.id}
            triggerRef={addRemoveWatchersTriggerRef}
            onClose={() => setShowModal(null)}
            onConfirm={handleAddRemoveWatchers}
            isSending={isUpdatingWatchers}
            assigneeId={simulationDetails.assignedTo.id}
          />
          <PriceDownloadModal
            isOpen={showModal === SIM_MODAL.Download}
            triggerRef={downloadTriggerRef}
            onClose={() => setShowModal(null)}
            onSubmit={(selectedDownloadOption) => {
              handleExportData(selectedDownloadOption as DownloadOption);
            }}
          />
          <PublishSimulationModal
            isOpen={showModal === SIM_MODAL.Publish}
            triggerRef={publishTriggerRef}
            onClose={() => setShowModal(null)}
            onConfirmPublish={handleConfirmPublish}
            isPublishing={isPublishing}
            currentStatus={simulationDetails.status}
          />
          <SimulationUploadModal
            isOpen={showModal === SIM_MODAL.Upload}
            triggerRef={downloadTriggerRef}
            planningYear={simulationDetails.planningYear}
            onClose={() => setShowModal(null)}
            onApply={(rows) => uploadApplyRef.current?.(rows)}
          />
        </PageWrapper>
      ) : isSimulationNotFound ? (
        <Navigate to={ROUTES.SimulationNotFound.path} />
      ) : (
        <FullSizeSpinner />
      )}
    </>
  );
};

export default SimulationToolPage;
